/**
 * Plugin host for Native Interactive Site Map.
 *
 * Manages the lifecycle of Native Interactive Site Map plugins: initialization, startup, and shutdown.
 * Each plugin receives a shared context containing root element, services, Redux store, etc.
 */

import runSequential from './pluginRunner';

export default function createPluginHost({
  root,
  plugins = [],
  config = {},
  services = {},
  state: initialState = {}
} = {}) {
  const registry = new Map();
  const orderedPlugins = [];

  const state = {
    initialized: false,
    started: false,
  };

  const context = {
    root,
    config,
    services: { ...services },
    state: { ...initialState },
  };

  function register(plugin) {
    if (!plugin?.name) {
      throw new Error('Plugins must expose a unique name.');
    }

    if (registry.has(plugin.name)) {
      throw new Error(`Plugin "${plugin.name}" is already registered.`);
    }

    registry.set(plugin.name, plugin);
    orderedPlugins.push(plugin);
  }

  async function init() {
    if (state.initialized) {
      return context;
    }

    await runSequential(orderedPlugins, 'init', context);

    state.initialized = true;
    return context;
  }

  /**
   * Starts all registered plugins.
   *
   * This method uses an "auto-init" pattern:
   * If the host has not been initialized yet, it will automatically
   * call `init()` before executing the `start` lifecycle.
   *
   * This ensures consumers can safely call `start()` without explicitly
   * invoking `init()` beforehand.
   */
  async function start() {
    if (state.started) {
      return context;
    }

    if (!state.initialized) {
      await init();
    }

    await runSequential(orderedPlugins, 'start', context);

    state.started = true;
    return context;
  }

  async function stop() {
    if (!state.started) {
      return context;
    }

    const reversed = orderedPlugins.slice().reverse();
    await runSequential(reversed, 'stop', context);

    state.started = false;
    return context;
  }

  const api = {
    register,
    init,
    start,
    stop,
  };

  plugins.forEach(register);

  return api;
}